import type { QueryClient } from '@tanstack/react-query'
import type { ApiClient } from '~/api/apiClient'
import type { PinV1 } from '~/pin/PinV1'
import { sanitizeFilename } from '~/pin/exportToJson'
import { buildContextCaption, buildExportMetadata } from '~/components/Charts/chrome/chartChrome'
import { replayUnitOf } from '~/pin/replayUnit'
import { foldRowsToUnit } from '~/components/Charts/foldWalkRows'
import { fetchPinData, timeframesOf, type PinData } from './fetchPinData'
import { resolveValueLabelsFor } from './labels'
import { flattenFlat } from './flattenFlat'
import { flattenWrapper } from './flattenWrapper'
import { flattenMultiWalk } from './flattenMultiWalk'
import { applyFyTimeColumns, exportPartialFyNote } from './fyTimeColumns'
import { clampFromToFloor, getModelFloorIso } from '~/state/modelFloor'
import type { SheetSpec } from './types'

type Deps = { client: ApiClient; queryClient: QueryClient }

// Excel worksheet-name hard limit.
const SHEET_NAME_MAX_LEN = 31

/** Campaigns C1 Task 9 (C3 fix-wave #10: rationale refreshed): renders a frozen per-arm
 *  filter_dims summary for the metadata block, e.g. 'Topic Level 3=kip; Brand Casting Layer
 *  Level 1=transmo' — or, on a fact that carries the exact Campaign dim, 'Campaign=1096' (the
 *  raw campaign id; the arm's own label elsewhere in the metadata block carries the human name).
 *  Raw values, no display-label resolution — these ARE the campaign's resolved filter values
 *  (the same strings resolvedCampaignFilters/predicateOf produce), not fact-scoped dim-catalog
 *  codes. */
function summarizeFilters(filterDims: Record<string, string[]>): string {
  return Object.entries(filterDims)
    .filter(([, values]) => values.length > 0)
    .map(([dim, values]) => `${dim}=${values.join(',')}`)
    .join('; ')
}

/** Dispatch on `PinData.kind` to the matching flattener (Tasks 1-3). Non-null assertions on the
 *  kind-specific payload fields are safe: fetchPinData always populates the field matching `kind`
 *  (see fetchPinData.ts's three return sites). */
function flattenPinData(
  data: PinData,
  resolveValue: (dim: string, value: string) => string,
): { columns: string[]; rows: Array<Record<string, unknown>> } {
  switch (data.kind) {
    case 'flat':
      return flattenFlat(data.single!, { hasTime: data.hasTime, resolveValue })
    case 'wrapper':
      return flattenWrapper(data.wrapper!)
    case 'multi':
      return flattenMultiWalk(data.slices!, { hasTime: data.hasTime, resolveValue })
  }
}

/** FY v2 (spec §4.2): fold fetched rows to the pin's display unit before flattening, so the
 *  sheet equals the chart to the cent. Identity skip when display unit === wire bucket (every
 *  legacy pin and every non-fy pin today). Engines mirror ChartFromPin: single walk_ratio rows
 *  and multi METRIC slices of a walk_ratio pin are ratio (Σnum/Σdenom); driver slices are
 *  always the timeline (volume) family; metric_driver_bars/absolute have no time axis
 *  (hasTime false → untouched); wrapper pins have no creation site that could record a
 *  render_unit, so they always identity-skip. */
function foldPinData(pin: PinV1, data: PinData): PinData {
  const unit = replayUnitOf(pin)
  const wireBucket = pin.walk_call.params.bucket_size ?? 'month'
  if (unit === wireBucket || !data.hasTime || data.kind === 'wrapper') return data
  const isRatioPin = pin.walk_call.function === 'walk_ratio'
  if (data.kind === 'flat') {
    return {
      ...data,
      single: foldRowsToUnit(data.single!, unit, isRatioPin ? 'ratio' : 'volume'),
    }
  }
  // kind 'multi'
  return {
    ...data,
    slices: data.slices!.map((s) => ({
      ...s,
      rows: foldRowsToUnit(
        s.rows,
        unit,
        s.arm === 'driver' ? 'volume' : isRatioPin ? 'ratio' : 'volume',
      ),
    })),
  }
}

/** PinV1 -> SheetSpec: re-runs the pin's walk(s) (fetchPinData), flattens the result to a table,
 *  and builds the XLSX metadata block + a sheet-safe name. The single per-pin entry point for
 *  data export (Task 8/9/10 wire this into the export menus). */
export async function pinToSheet(pin: PinV1, deps: Deps): Promise<SheetSpec> {
  const data = await fetchPinData(pin, deps)
  const folded = foldPinData(pin, data)
  const resolveValue = await resolveValueLabelsFor(folded.factTable, deps)
  let table = flattenPinData(folded, resolveValue)
  let fyNoteRow: Array<[string, string]> = []
  if (replayUnitOf(pin) === 'fy' && folded.hasTime && folded.kind !== 'wrapper') {
    // Windows = what the walks were FIRED with: fetchPinData floor-clamps `from`
    // (fetchPinData.ts:59-61) — the marker judges the fetched window (v1 final-review I1).
    const floor = getModelFloorIso()
    const clampW = (from: string, to: string) => ({ from: clampFromToFloor(from, floor), to })
    if (folded.kind === 'multi') {
      const byLabel = new Map(
        timeframesOf(pin).map((tf) => [tf.label || tf.id, clampW(tf.from, tf.to)]),
      )
      table = applyFyTimeColumns(table, (row) => {
        const w = byLabel.get(String(row.Timeframe))
        if (!w) {
          throw new Error(
            `pinToSheet: no timeframe window for sheet row label "${String(row.Timeframe)}"`,
          )
        }
        return w
      })
      const note = exportPartialFyNote([...byLabel.values()])
      if (note) fyNoteRow = [['Note', note]]
    } else {
      const w = clampW(pin.walk_call.params.from, pin.walk_call.params.to)
      table = applyFyTimeColumns(table, () => w)
      const note = exportPartialFyNote([w])
      if (note) fyNoteRow = [['Note', note]]
    }
  }
  const { columns, rows } = table

  const title = pin.render_hint.title ?? pin.walk_call.kpi
  const captionLines = buildContextCaption({
    filters: pin.walk_call.params.filter_dims,
    groupBy: pin.walk_call.params.group_by,
    valueLabelFor: resolveValue,
  })
  // Pins carry no preset label (nothing survives into walk_call); buildExportMetadata shows the
  // concrete from/to range on its own when timeframePhrase is empty.
  const metadata = buildExportMetadata({
    title,
    kpi: pin.walk_call.kpi,
    timeframePhrase: '',
    fromIso: pin.walk_call.params.from,
    toIso: pin.walk_call.params.to,
    factTable: data.factTable,
    captionLines,
  })

  // Campaigns C1 Task 9: self-describing sheet — a pin timeframe entry with a frozen per-arm
  // predicate (Task 8) gets its own metadata row, so a reader can tell campaign arms apart from
  // the base filters already captioned above. No-op for pins with no per-arm filter_dims (the
  // vast majority — single-timeframe or campaign-free multi-timeframe pins).
  const tfMetadata: Array<[string, string]> = timeframesOf(pin)
    .filter((tf) => tf.filter_dims)
    .map((tf) => [`Timeframe ${tf.label || tf.id}`, summarizeFilters(tf.filter_dims!)])

  // FU-03: driver slices fire the fact-valid subset of the frozen filters (fetchPinData strips
  // via pin/driverArmFilters). Self-describe what actually fired: one row per driver slice
  // whose effective filters differ from the frozen ones, plus an omission row per
  // campaign-blocked arm. No-op for non-driver pins and for driver slices that strip to
  // themselves (the common case).
  const driverMetadata: Array<[string, string]> = (data.slices ?? [])
    .filter((s) => s.arm === 'driver' && s.dropped && s.dropped.length > 0)
    .map((s): [string, string] => [
      `Driver arm ${s.timeframe}`,
      `${summarizeFilters(s.effective ?? {}) || 'no filters'}; not applicable to driver fact: ${s.dropped!.join(', ')}`,
    ])
  const blockedMetadata: Array<[string, string]> = (data.driverBlocked ?? []).map(
    (label): [string, string] => [
      `Driver arm ${label}`,
      'campaign filter not applicable to driver fact — driver data omitted',
    ],
  )

  const sheetName = sanitizeFilename(title).slice(0, SHEET_NAME_MAX_LEN).trim()

  return {
    sheetName,
    metadata: [...metadata, ...tfMetadata, ...driverMetadata, ...blockedMetadata, ...fyNoteRow],
    columns,
    rows,
  }
}
